import TextareaAutosize from "react-textarea-autosize";
import { CanadianButtonSmall } from "../ui-components/CanadianButton";
import { Fragment, useRef, useState } from "react";
import { SERVER_URL } from "../api/client";
import { useThrottleFn } from "../hooks/useThrottleFn";
import { useTranslation } from "react-i18next";
import { Player } from "@lottiefiles/react-lottie-player";
import lottieJSON from "../assets/lottie/1749405124755.json";
import { Link } from "react-router-dom";
import { FaRegShareSquare } from "react-icons/fa";
import { Helmet } from "react-helmet-async";

const ThinkingAnimation = () => (
  <Player
    autoplay
    loop
    src={lottieJSON}
    style={{ height: "200px", width: "200px" }}
  />
);

const roleClass = {
  assistant: "avatar-assistant",
  user: "avatar-user",
};

const MessageItem = ({ message, role, post }) => {
  const modifiedMessage = message.replace(/DeepSeek(-R1)?/g, "LoonAi");

  return (
    <div
      className={`message-container ${
        role === "user" ? "flex-row-reverse" : ""
      }`}
    >
      <div className={`ai-avatar ${roleClass[role]}`}>
        {role === "assistant" && <span className="avatar-icon">ꪶꪶ</span>}
        {role === "user" && <span className="avatar-icon">U</span>}
      </div>
      <div
        className={`message-bubble ${
          role === "user" ? "bubble-user" : "bubble-assistant"
        }`}
      >
        {modifiedMessage}
      </div>
      {post && (
        <Link to={`/post?post=${encodeURIComponent(modifiedMessage)}`}>
          <FaRegShareSquare size={24} />
        </Link>
      )}
    </div>
  );
};

const AI = () => {
  const { t } = useTranslation();

  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [currentError, setCurrentError] = useState();
  const [currentAssistantMessage, setCurrentAssistantMessage] = useState("");
  const [controller, setController] = useState();

  const [messageList, setMessageList] = useState([]);
  const handleButtonClick = async () => {
    if (!inputRef.current) return;

    const inputValue = inputRef.current.value;

    inputRef.current.value = "";

    const messages = [
      ...messageList,
      {
        role: "user",
        content: inputValue,
      },
    ];

    setMessageList(messages);

    requestWithLatestMessage(messages);
  };

  const requestWithLatestMessage = async (messages) => {
    setLoading(true);
    setCurrentAssistantMessage("");
    setCurrentError(null);
    try {
      const controller = new AbortController();
      setController(controller);
      const response = await fetch(SERVER_URL + "/ai/generate", {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify({
          messages,
        }),
        signal: controller.signal,
        credentials: "include",
      });
      if (!response.ok) {
        const error = await response.json();
        console.error(error.error);
        setCurrentError(error.error);
        throw new Error("Request failed");
      }

      let message = currentAssistantMessage;

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let done = false;
      while (!done) {
        const { value, done: readerDone } = await reader.read();
        if (value) {
          const char = decoder.decode(value, { stream: true });
          if (char === "\n" && currentAssistantMessage.endsWith("\n")) continue;

          if (char) {
            message += char;
            setCurrentAssistantMessage(message);
          }

          smoothToBottom();
        }
        done = readerDone;
        if (readerDone) archiveCurrentMessage(messages, message);
      }
    } catch (e) {
      console.error(e);
      setLoading(false);
      setController(null);
      return;
    }
  };

  const archiveCurrentMessage = (
    messages = messageList,
    message = currentAssistantMessage
  ) => {
    if (message) {
      setMessageList([
        ...messages,
        {
          role: "assistant",
          content: message,
        },
      ]);
      setCurrentAssistantMessage("");
      setLoading(false);
      setController(null);
      inputRef.current?.focus();
    }
  };
  const smoothToBottom = useThrottleFn(
    () => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    },
    300,
    false,
    true
  );

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="ai">
        <div className="ai-messages">
          {messageList.map((message) => (
            <MessageItem
              key={message.content}
              role={message.role}
              message={message.content}
              post={message.role === "assistant"}
            />
          ))}
          {currentAssistantMessage && (
            <MessageItem role="assistant" message={currentAssistantMessage} />
          )}
        </div>
        {loading ? (
          <div
            style={{
              width: "100%",
              height: "130px",
              background: "#f3f3f3",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                fontSize: "1.2em",
                fontWeight: "700",
              }}
            >
              {/* {t("aiLoadingThinking")} */}
              <ThinkingAnimation />
            </span>
          </div>
        ) : (
          <Fragment>
            <div className="display-flex justify-content-sb align-items-c">
              <TextareaAutosize
                ref={inputRef}
                placeholder={t("aiPlaceholder")}
              />
              <CanadianButtonSmall onClick={handleButtonClick}>
                {t("aiButtonAsk")}
              </CanadianButtonSmall>
            </div>
          </Fragment>
        )}
      </div>
    </>
  );
};

export default AI;
